import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiSend } from '../utils/api';

// The bell. Polls the in-app notification surface, shows an unread badge, and opens a simple list
// panel. Styling mirrors the existing sidebar / inline-style palette (var(--*)) — no new design
// system. Clicking a notification marks it read and, if it carries a deal_id, jumps to that deal.

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const secs = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return 'just now';
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const navigate = useNavigate();
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await apiGet('/api/notifications?limit=30');
      setItems(d.notifications || []);
      setUnread(d.unread || 0);
    } catch { /* unauthenticated / transient — leave state as-is */ }
  }, []);

  // Poll every 60s, and refresh on open so the panel is current.
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const toggle = () => { const next = !open; setOpen(next); if (next) load(); };

  const onItemClick = async (n) => {
    if (!n.read_at) {
      try { await apiSend(`/api/notifications/${n.id}/read`, { method: 'POST' }); } catch { /* non-fatal */ }
      setItems(prev => prev.map(x => x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x));
      setUnread(u => Math.max(0, u - 1));
    }
    // Email notifications (a reply arrived, a follow-up reminder is due) open the deal's Email panel.
    // The old campaign builder is retired — follow-ups are reminders the user sends from the deal.
    if (n.type === 'gmail_revoked') { setOpen(false); navigate('/settings'); return; }
    if (n.type.startsWith('gmail_') && n.deal_id) { setOpen(false); navigate(`/deals/${n.deal_id}#email`); return; }
    if (n.deal_id) { setOpen(false); navigate(`/underwriting/${n.deal_id}`); }
  };

  const markAll = async () => {
    try { await apiSend('/api/notifications/read-all', { method: 'POST' }); } catch { /* non-fatal */ }
    setItems(prev => prev.map(x => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
    setUnread(0);
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        onClick={toggle}
        title="Notifications"
        aria-label={`Notifications${unread ? ` (${unread} unread)` : ''}`}
        style={{
          position: 'relative', width: 34, height: 34, borderRadius: 8,
          background: open ? 'var(--teal-dim)' : 'transparent',
          border: '1px solid var(--navy-border)', cursor: 'pointer',
          color: open ? 'var(--teal)' : 'var(--gray-300)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: -4, right: -4, minWidth: 16, height: 16, padding: '0 4px',
            borderRadius: 8, background: 'var(--red)', color: '#fff', fontSize: 10, fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}>{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div style={{
          // Anchor the panel's LEFT edge to the bell so it opens rightward into the main content
          // area. (right:0 opened it leftward, off the left viewport edge — the bell sits at the far
          // left of the screen inside the sidebar — which clipped the panel under the sidebar.)
          position: 'absolute', top: 42, left: 0, width: 320, maxHeight: 420, overflowY: 'auto',
          background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 10,
          boxShadow: '0 12px 32px rgba(0,0,0,0.45)', zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--navy-border)' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--white)' }}>Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} style={{ background: 'transparent', border: 'none', color: 'var(--teal)', fontSize: 12, cursor: 'pointer', padding: 0 }}>
                Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <div style={{ padding: '28px 14px', textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>
              You're all caught up.
            </div>
          ) : items.map(n => (
            <div
              key={n.id}
              onClick={() => onItemClick(n)}
              style={{
                padding: '11px 14px', borderBottom: '1px solid var(--navy-border)', cursor: 'pointer',
                background: n.read_at ? 'transparent' : 'var(--teal-dim)',
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', marginTop: 5, flexShrink: 0, background: n.read_at ? 'transparent' : 'var(--teal)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--white)' }}>{n.title}</div>
                {n.body && <div style={{ fontSize: 12, color: 'var(--gray-300)', marginTop: 2, lineHeight: 1.4 }}>{n.body}</div>}
                <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 4 }}>{relTime(n.created_at)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}
